/* Shared UK presentation; Shopify remains authoritative for instalment offers. */
(() => {
  'use strict';
  if (customElements.get('uk-payment-options')) return;

  class UKPaymentOptions extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.wrapper = this.closest('[data-uk-shop-pay]');
      if (!this.wrapper) return;
      this.readyObserver = new MutationObserver(() => this.initialize());
      this.readyObserver.observe(this.wrapper, {
        attributes: true, attributeFilter: ['data-uk-shop-pay-initialized']
      });
      this.initialize();
    }

    initialize() {
      if (this.initialized || this.wrapper.dataset.ukShopPayInitialized !== 'true') return;
      this.root = this.wrapper.closest('.product-section__individual') ||
        document.getElementById('page-content');
      this.shop = this.wrapper.querySelector('shopify-payment-terms');
      this.launch = this.querySelector('[data-uk-options-open]');
      this.template = this.querySelector('[data-uk-options-template]');
      if (!this.root || !this.shop || !this.launch || !this.template) return;
      try {
        this.variants = JSON.parse(this.querySelector('[data-uk-options-variants]').textContent);
      } catch (_) { return; }
      this.initialized = true;
      this.readyObserver.disconnect();
      this.onKeydown = this.onKeydown.bind(this);
      this.onUnload = this.onUnload.bind(this);
      this.onLaunch = () => this.open();
      this.launch.addEventListener('click', this.onLaunch);
      this.variantObserver = new MutationObserver(() => this.updatePrice());
      this.variantObserver.observe(this.shop, { attributes: true, attributeFilter: ['variant-id'] });
      document.addEventListener('shopify:section:unload', this.onUnload);
      this.hidden = false;
      this.replaceLegacyPresentation();
    }

    replaceLegacyPresentation() {
      this.replaced = [];
      // Only PDP finance entries, never a membership block or page-level finance section.
      const legacy = [...this.root.querySelectorAll('[data-pdp24-financing], [data-uk-equipment-finance], [data-uk-compact-newpay]')];
      this.root.querySelectorAll('.product-price').forEach(element => {
        if (element.querySelector('.btn-finance-popup') ||
          (/Finance options/i.test(element.textContent) && /Newpay/i.test(element.textContent)) ||
          /^\s*Monthly\s+from\b/i.test(element.textContent)) {
          legacy.push(element);
        }
      });
      legacy.forEach(element => {
        if (element.closest('uk-payment-options') || element.hasAttribute('data-uk-payment-replaced')) return;
        element.setAttribute('data-uk-payment-replaced', '');
        this.replaced.push(element);
        const divider = element.previousElementSibling;
        if (divider?.matches('.or-divider, .product-price__splitter')) {
          divider.setAttribute('data-uk-payment-replaced', '');
          this.replaced.push(divider);
        }
      });
    }

    mount() {
      if (this.overlay) return true;
      if (!this.shop.isConnected) return false;
      this.marker = document.createComment('uk-native-terms-original-position');
      this.shop.before(this.marker);
      const fragment = this.template.content.cloneNode(true);
      this.overlay = fragment.querySelector('.uk-payment-options__overlay');
      this.panel = this.overlay.querySelector('[role="dialog"]');
      this.closeButton = this.overlay.querySelector('[data-uk-options-close]');
      this.overlay.addEventListener('click', event => {
        if (event.target === this.overlay || event.target.closest('[data-uk-options-close]')) this.close();
        if (event.target.closest('[data-uk-options-shop-open]')) {
          this.nativeButton()?.click();
        }
        const newpay = event.target.closest('[data-uk-options-newpay-open]');
        if (newpay) {
          const details = this.overlay.querySelector('.uk-payment-options__newpay-details');
          details.hidden = !details.hidden;
          newpay.setAttribute('aria-expanded', String(!details.hidden));
          if (!details.hidden) {
            details.querySelector('h3').focus({ preventScroll: true });
            details.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }
      });
      document.body.append(fragment);
      return true;
    }

    nativeButton() {
      return this.shop.shadowRoot?.querySelector('button[aria-haspopup="dialog"]');
    }

    refreshShopAction() {
      if (!this.overlay) return;
      const ready = !!this.nativeButton();
      this.overlay.querySelector('[data-uk-options-shop-open]').disabled = !ready;
      this.overlay.querySelector('[data-uk-options-unavailable]').hidden = ready;
    }

    updatePrice() {
      if (!this.overlay || this.overlay.hidden) return;
      const variant = this.variants.find(item => String(item.id) === this.shop.getAttribute('variant-id'));
      if (!variant) return; // A membership ID can never replace the product quote.
      const selector = '.individual_product__price [data-product-price], .scm-current-price, .fitquest-pdp__price';
      const price = this.root.querySelector(selector);
      this.overlay.querySelector('[data-uk-options-price]').textContent =
        price?.textContent.trim() || variant.priceLabel;
    }

    open() {
      if (!this.mount() || !this.overlay.hidden) return;
      this.previousOverflow = document.body.style.overflow;
      this.overlay.querySelector('[data-uk-options-shop]').append(this.shop);
      this.overlay.hidden = false;
      this.launch.setAttribute('aria-expanded', 'true');
      this.updatePrice();
      this.refreshShopAction();
      // Native custom elements can repaint their shadow tree after being moved.
      this.shadowObserver = new MutationObserver(() => this.refreshShopAction());
      if (this.shop.shadowRoot) {
        this.shadowObserver.observe(this.shop.shadowRoot, { childList: true, subtree: true });
      }
      document.body.style.overflow = 'hidden';
      this.inerted = [];
      this.inertSiblings();
      this.bodyObserver = new MutationObserver(() => this.inertSiblings());
      this.bodyObserver.observe(document.body, { childList: true });
      document.addEventListener('keydown', this.onKeydown);
      this.closeButton.focus();
    }

    inertSiblings() {
      for (const element of document.body.children) {
        if (element === this.overlay || element.inert || ['SCRIPT', 'STYLE'].includes(element.tagName)) continue;
        if (element.matches('[data-nametag="shop-portal-provider"]')) continue;
        element.inert = true;
        this.inerted.push(element);
      }
    }

    nativeDialogOpen() {
      return [...document.querySelectorAll('[data-nametag="shop-portal-provider"]')]
        .some(portal => portal.shadowRoot?.querySelector('[data-testid="authorize-modal"][data-visible="true"]'));
    }

    close(restoreFocus = true) {
      if (!this.overlay || this.overlay.hidden) return;
      this.shadowObserver?.disconnect();
      this.bodyObserver?.disconnect();
      if (this.marker.isConnected) this.marker.after(this.shop);
      this.overlay.hidden = true;
      this.launch.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = this.previousOverflow;
      this.inerted.forEach(element => { element.inert = false; });
      this.inerted = [];
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus && this.launch.isConnected) this.launch.focus();
    }

    onKeydown(event) {
      if (event.defaultPrevented || this.nativeDialogOpen() || !event.composedPath().includes(this.overlay)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key !== 'Tab') return;
      const items = [...this.overlay.querySelectorAll('button:not([disabled]), a[href], summary, shopify-payment-terms')]
        .flatMap(element => element.shadowRoot
          ? [...element.shadowRoot.querySelectorAll('button:not([disabled]), a[href]')]
          : [element])
        .filter(element => element.getClientRects().length > 0 && !element.closest('[hidden]'));
      const active = this.shop.shadowRoot?.activeElement || document.activeElement;
      if (event.shiftKey && active === items[0]) {
        event.preventDefault();
        items[items.length - 1]?.focus();
      } else if (!event.shiftKey && active === items[items.length - 1]) {
        event.preventDefault();
        items[0]?.focus();
      }
    }

    onUnload(event) {
      if (event.target.contains(this)) this.cleanup();
    }

    cleanup() {
      this.close(false);
      this.readyObserver?.disconnect();
      this.variantObserver?.disconnect();
      this.overlay?.remove();
      this.marker?.remove();
      this.replaced?.forEach(element => element.removeAttribute('data-uk-payment-replaced'));
      this.launch?.removeEventListener('click', this.onLaunch);
      document.removeEventListener('shopify:section:unload', this.onUnload);
    }

    disconnectedCallback() {
      this.cleanup();
      this.started = false;
      this.initialized = false;
      this.overlay = null;
    }
  }

  customElements.define('uk-payment-options', UKPaymentOptions);
})();
